import { useState } from 'react';
import type { AssessmentQuestion } from '@/lib/types';
import { inlineMarkdown } from '@/lib/markdown';

interface MatchingProps {
  question: AssessmentQuestion;
  onAnswer: (answer: Record<string, string>) => void;
  disabled: boolean;
  currentAnswer?: Record<string, string>;
}

export function Matching({ question, onAnswer, disabled, currentAnswer }: MatchingProps) {
  const [assignments, setAssignments] = useState<Record<string, string>>(
    currentAnswer || {}
  );
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const categories = question.categories || [];
  const items = question.items || [];

  const handleItemClick = (itemId: string) => {
    if (disabled) return;

    if (selectedItem === itemId) {
      // Deselect
      setSelectedItem(null);
    } else if (assignments[itemId]) {
      // Already assigned — clicking unassigns it
      const newAssignments = { ...assignments };
      delete newAssignments[itemId];
      setAssignments(newAssignments);
      onAnswer(newAssignments);
      setSelectedItem(null);
    } else {
      // Select this item to assign
      setSelectedItem(itemId);
    }
  };

  const handleCategoryClick = (category: string) => {
    if (disabled || !selectedItem) return;

    const newAssignments = { ...assignments, [selectedItem]: category };
    setAssignments(newAssignments);
    onAnswer(newAssignments);
    setSelectedItem(null);
  };

  // Get items assigned to a category
  const getItemsInCategory = (category: string) =>
    items.filter((item) => assignments[item.id] === category);

  // Get unassigned items
  const unassignedItems = items.filter((item) => !assignments[item.id]);

  // Category colors
  const categoryColors: Record<number, { bg: string; border: string; badge: string }> = {
    0: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-300 dark:border-blue-700',
      badge: 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
    },
    1: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-300 dark:border-emerald-700',
      badge: 'bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200',
    },
  };

  return (
    <div>
      {/* Question header */}
      <h3 className="text-xl font-serif font-semibold mb-2 text-slate-900 dark:text-slate-100"
          dangerouslySetInnerHTML={{ __html: inlineMarkdown(question.question) }} />
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Click a scenario, then click a category to assign it. Click an assigned scenario to unassign.
      </p>

      {/* Unassigned scenarios */}
      {unassignedItems.length > 0 && (
        <div className="mb-8">
          <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
            Scenarios to classify
          </h4>
          <div className="space-y-2">
            {unassignedItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                disabled={disabled}
                className={`
                  w-full text-left p-4 rounded-lg border-2 transition-all
                  ${
                    selectedItem === item.id
                      ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 shadow-md scale-[1.01]'
                      : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 hover:shadow-sm'
                  }
                  ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <span className="text-slate-700 dark:text-slate-300"
                      dangerouslySetInnerHTML={{ __html: inlineMarkdown(item.text) }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selection hint */}
      {selectedItem && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-center">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Now click a category below to assign this scenario
          </p>
        </div>
      )}

      {/* Category columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((category, catIndex) => {
          const colors = categoryColors[catIndex] || categoryColors[0];
          const assignedItems = getItemsInCategory(category);

          return (
            <div
              key={category}
              onClick={() => handleCategoryClick(category)}
              className={`
                rounded-xl border-2 p-4 min-h-[200px] transition-all
                ${colors.bg} ${colors.border}
                ${
                  selectedItem && !disabled
                    ? 'cursor-pointer ring-2 ring-amber-300 dark:ring-amber-600 hover:shadow-lg'
                    : ''
                }
              `}
            >
              {/* Category header */}
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colors.badge}`}>
                  {category}
                </span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {assignedItems.length} assigned
                </span>
              </div>

              {/* Assigned items */}
              <div className="space-y-2">
                {assignedItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item.id);
                    }}
                    className={`
                      p-3 rounded-lg bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-600
                      text-sm text-slate-700 dark:text-slate-300
                      ${!disabled ? 'cursor-pointer hover:bg-white dark:hover:bg-slate-800' : ''}
                    `}
                    dangerouslySetInnerHTML={{ __html: inlineMarkdown(item.text) }}
                  />
                ))}

                {assignedItems.length === 0 && !selectedItem && (
                  <p className="text-sm text-slate-400 dark:text-slate-500 italic text-center py-6">
                    No scenarios assigned yet
                  </p>
                )}

                {assignedItems.length === 0 && selectedItem && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 text-center py-6 animate-pulse">
                    Click here to assign the selected scenario
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress */}
      <div className="mt-6 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
        <span>
          {Object.keys(assignments).length} of {items.length} scenarios classified
        </span>
        {Object.keys(assignments).length === items.length && (
          <span className="text-green-600 dark:text-green-400 font-medium">
            All scenarios classified
          </span>
        )}
      </div>
    </div>
  );
}
